/**
 * resolveFormTemplates.js
 *
 * Resolves {{patientFirstName}} tokens in Adult Autism informant form display text
 * at render time. Tokens are stored literally in Firestore form definitions so one
 * template serves all patients; the patient's first name is injected when the
 * informant views the form.
 *
 * Design decisions (plain English):
 *
 * 1. **Two token shapes** — templates use either a bare subject token
 *    (`{{patientFirstName}}`) or a possessive (`{{patientFirstName}}'s`). We always
 *    replace the longer possessive form first so we never leave a stray `'s` behind.
 *
 * 2. **String split/join, not regex** — patient names may contain apostrophes
 *    (O'Brien), hyphens (Mary-Jane), or other characters that would break regex
 *    replacement. Split/join on the fixed token literals is safe regardless of name.
 *
 * 3. **Fallback when name is missing** — before the patient's name is known we use
 *    neutral third-person phrasing that matches how the informant forms were adapted:
 *    - Subject: "the person being assessed" (e.g. "Has the person being assessed ever…")
 *    - Possessive: "their" (e.g. "This section is about their wellbeing")
 *    This avoids broken grammar like "the person being assessed's wellbeing".
 *
 * 4. **UK possessive with a real name** — we always append `'s` (James's, Chris's).
 *    This matches UK clinical writing conventions used elsewhere in the product.
 *
 * 5. **Deep clone before resolve** — callers receive a new formDefinition object;
 *    the stored Firestore template is never mutated in memory.
 */

/** Literal token stored in informant form definitions. */
export const PATIENT_FIRST_NAME_TOKEN = '{{patientFirstName}}';

/** Possessive token — always `{{patientFirstName}}'s` in our schemas. */
export const PATIENT_FIRST_NAME_POSSESSIVE_TOKEN = `${PATIENT_FIRST_NAME_TOKEN}'s`;

/** Subject fallback when patientFirstName is empty or not yet loaded. */
export const PATIENT_FIRST_NAME_SUBJECT_FALLBACK = 'the person being assessed';

/** Possessive fallback — grammatically correct substitute for `Name's`. */
export const PATIENT_FIRST_NAME_POSSESSIVE_FALLBACK = 'their';

/** Display fields that may contain template tokens (mirrors informant_adapt.py). */
const TEXT_FIELDS = new Set([
  'formTitle',
  'formExplanationText',
  'formSubsectionTitle',
  'questionTitle',
  'questionTooltip',
  'subquestionDisplayText',
  'placeholder',
]);

const NESTED_TEXT_DICT_FIELDS = new Set(['otherOptions', 'suchAsText']);
const OPTION_LIST_FIELDS = new Set(['options', 'noneOptions']);

/**
 * Normalise a patient first name for injection. Returns empty string when absent.
 */
export function normalisePatientFirstName(patientFirstName) {
  if (patientFirstName === undefined || patientFirstName === null) return '';
  const trimmed = String(patientFirstName).trim();
  return trimmed;
}

/**
 * Build the subject replacement (bare token) for a given first name.
 */
export function resolveSubjectReplacement(patientFirstName) {
  const name = normalisePatientFirstName(patientFirstName);
  return name || PATIENT_FIRST_NAME_SUBJECT_FALLBACK;
}

/**
 * Build the possessive replacement for a given first name.
 * Uses "their" when the name is missing so sentences stay grammatical.
 */
export function resolvePossessiveReplacement(patientFirstName) {
  const name = normalisePatientFirstName(patientFirstName);
  if (!name) return PATIENT_FIRST_NAME_POSSESSIVE_FALLBACK;
  return `${name}'s`;
}

/**
 * Replace {{patientFirstName}} and {{patientFirstName}}'s in a single string.
 *
 * @param {string|null|undefined} text
 * @param {string} patientFirstName
 * @returns {string|null|undefined}
 */
export function resolvePatientFirstNameInText(text, patientFirstName) {
  if (!text || typeof text !== 'string') return text;
  if (!text.includes(PATIENT_FIRST_NAME_TOKEN)) return text;

  const possessiveReplacement = resolvePossessiveReplacement(patientFirstName);
  const subjectReplacement = resolveSubjectReplacement(patientFirstName);

  // Possessive token first — prevents leaving a dangling 's after bare replacement.
  let result = text.split(PATIENT_FIRST_NAME_POSSESSIVE_TOKEN).join(possessiveReplacement);
  result = result.split(PATIENT_FIRST_NAME_TOKEN).join(subjectReplacement);
  return result;
}

/**
 * Returns true when unresolved template tokens remain (used by tests and dev guards).
 */
export function hasUnresolvedTemplateTokens(text) {
  if (!text || typeof text !== 'string') return false;
  return text.includes(PATIENT_FIRST_NAME_TOKEN);
}

/**
 * Walk a nested text dict (e.g. otherOptions) and resolve tokens in string values.
 */
function resolveNestedTextDict(dict) {
  if (!dict || typeof dict !== 'object') return dict;
  const resolved = {};
  Object.entries(dict).forEach(([key, value]) => {
    resolved[key] = typeof value === 'string' ? value : value;
  });
  return resolved;
}

/**
 * Resolve tokens in a string array (e.g. selectMultiple options).
 */
function resolveStringList(list, patientFirstName) {
  if (!Array.isArray(list)) return list;
  return list.map((item) => (
    typeof item === 'string' ? resolvePatientFirstNameInText(item, patientFirstName) : item
  ));
}

/**
 * Resolve tokens on a single subquestion object.
 */
function resolveSubquestion(subquestion, patientFirstName) {
  if (!subquestion || typeof subquestion !== 'object') return subquestion;
  const resolved = { ...subquestion };

  TEXT_FIELDS.forEach((field) => {
    if (typeof resolved[field] === 'string') {
      resolved[field] = resolvePatientFirstNameInText(resolved[field], patientFirstName);
    }
  });

  NESTED_TEXT_DICT_FIELDS.forEach((field) => {
    if (resolved[field]) {
      const nested = resolveNestedTextDict(resolved[field]);
      const withTokens = {};
      Object.entries(nested).forEach(([k, v]) => {
        withTokens[k] = typeof v === 'string'
          ? resolvePatientFirstNameInText(v, patientFirstName)
          : v;
      });
      resolved[field] = withTokens;
    }
  });

  OPTION_LIST_FIELDS.forEach((field) => {
    if (resolved[field]) {
      resolved[field] = resolveStringList(resolved[field], patientFirstName);
    }
  });

  return resolved;
}

/**
 * Deep-clone a formDefinition and resolve all display-text template tokens.
 *
 * @param {object} formDefinition - Raw formDefinition from a formInstance document
 * @param {string} patientFirstName - Patient's first name for injection
 * @returns {object} New formDefinition with tokens resolved
 */
export function resolveFormDefinitionTemplates(formDefinition, patientFirstName) {
  if (!formDefinition || typeof formDefinition !== 'object') return formDefinition;

  const resolved = JSON.parse(JSON.stringify(formDefinition));

  TEXT_FIELDS.forEach((field) => {
    if (typeof resolved[field] === 'string') {
      resolved[field] = resolvePatientFirstNameInText(resolved[field], patientFirstName);
    }
  });

  Object.entries(resolved.formSubsections || {}).forEach(([, subsection]) => {
    if (typeof subsection.formSubsectionTitle === 'string') {
      subsection.formSubsectionTitle = resolvePatientFirstNameInText(
        subsection.formSubsectionTitle,
        patientFirstName,
      );
    }

    Object.entries(subsection.questions || {}).forEach(([, question]) => {
      TEXT_FIELDS.forEach((field) => {
        if (typeof question[field] === 'string') {
          question[field] = resolvePatientFirstNameInText(question[field], patientFirstName);
        }
      });

      NESTED_TEXT_DICT_FIELDS.forEach((field) => {
        if (question[field]) {
          const withTokens = {};
          Object.entries(question[field]).forEach(([k, v]) => {
            withTokens[k] = typeof v === 'string'
              ? resolvePatientFirstNameInText(v, patientFirstName)
              : v;
          });
          question[field] = withTokens;
        }
      });

      Object.entries(question.subquestions || {}).forEach(([sqKey, sq]) => {
        question.subquestions[sqKey] = resolveSubquestion(sq, patientFirstName);
      });
    });
  });

  return resolved;
}

/**
 * Resolve tokens on a formInstance for display (title, explanation, definition).
 * Returns a shallow copy of the instance with resolved display fields.
 */
export function resolveFormInstanceForDisplay(formInstance, patientFirstName) {
  if (!formInstance) return formInstance;

  return {
    ...formInstance,
    formTitle: resolvePatientFirstNameInText(formInstance.formTitle, patientFirstName),
    formExplanationText: resolvePatientFirstNameInText(
      formInstance.formExplanationText,
      patientFirstName,
    ),
    formDefinition: formInstance.formDefinition
      ? resolveFormDefinitionTemplates(formInstance.formDefinition, patientFirstName)
      : formInstance.formDefinition,
  };
}

/**
 * Create a display-text resolver bound to a patient first name.
 * Suitable for FormTemplateContext consumers (GenericFormPage, dashboard cards).
 */
export function createTemplateTextResolver(patientFirstName) {
  return (text) => resolvePatientFirstNameInText(text, patientFirstName);
}
